import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Zap, 
  Calendar, 
  Clock, 
  Users, 
  TrendingUp, 
  CheckCircle, 
  AlertTriangle,
  Sparkles,
  BarChart3
} from "lucide-react";
import { Event, EventOption } from "@/types/event";
import { AvailabilityStatus } from "./AvailabilityIndicator";
import { useSmartVoting } from "@/hooks/useSmartVoting";

interface SmartVotingFeaturesProps {
  event: Event;
  guestId: string;
  availabilityData: Record<string, AvailabilityStatus>;
  onQuickVoteAvailable: () => void;
  onSuggestedOptionsSelect: (optionIds: string[]) => void;
  showHeatmap?: boolean;
}

interface OptionAnalysis {
  optionId: string;
  option: EventOption;
  availabilityScore: number;
  conflictCount: number;
  isRecommended: boolean;
}

export const SmartVotingFeatures: React.FC<SmartVotingFeaturesProps> = ({
  event,
  guestId,
  availabilityData,
  onQuickVoteAvailable,
  onSuggestedOptionsSelect,
  showHeatmap = false
}) => {
  const { isFeatureEnabled } = useSmartVoting();
  const [optionAnalysis, setOptionAnalysis] = useState<OptionAnalysis[]>([]);
  const [bestTimeSlots, setBestTimeSlots] = useState<string[]>([]);
  const [groupAvailability, setGroupAvailability] = useState<Record<string, number>>({});

  // Calculate smart recommendations
  useEffect(() => {
    const analyzeOptions = () => {
      const analysis: OptionAnalysis[] = event.options.map(option => {
        const availability = availabilityData[option.id] || 'unknown';
        
        // Calculate availability score (higher is better)
        let availabilityScore = 0;
        switch (availability) {
          case 'free':
            availabilityScore = 100;
            break;
          case 'tentative':
            availabilityScore = 60;
            break;
          case 'busy':
            availabilityScore = 20;
            break;
          case 'not_connected':
          case 'unknown':
            availabilityScore = 50; // neutral
            break;
        }

        return {
          optionId: option.id,
          option,
          availabilityScore,
          conflictCount: availability === 'busy' ? 1 : availability === 'tentative' ? 0.5 : 0,
          isRecommended: availabilityScore >= 80
        };
      });

      setOptionAnalysis(analysis);

      // Identify best time slots (top available options)
      const sortedByAvailability = analysis
        .filter(a => a.availabilityScore >= 60)
        .sort((a, b) => b.availabilityScore - a.availabilityScore)
        .slice(0, 3)
        .map(a => a.optionId);

      setBestTimeSlots(sortedByAvailability);
    };

    analyzeOptions();
  }, [event.options, availabilityData]);

  // Fetch real group availability data
  useEffect(() => {
    const fetchGroupAvailability = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('group-availability', {
          body: { event_id: event.id }
        });

        if (error) {
          console.error('Error fetching group availability:', error);
          return;
        }

        if (data?.success && data.group_availability) {
          setGroupAvailability(data.group_availability);
        }
      } catch (error) {
        console.error('Failed to fetch group availability:', error);
      }
    };

    if (showHeatmap) {
      fetchGroupAvailability();
    }
  }, [event.id, showHeatmap]);

  const handleQuickVoteAvailable = () => {
    const availableOptions = optionAnalysis
      .filter(analysis => analysis.isRecommended)
      .map(analysis => analysis.optionId);
    
    onSuggestedOptionsSelect(availableOptions);
    onQuickVoteAvailable();
  };

  const formatOptionValue = (option: EventOption) => {
    switch (option.type) {
      case 'date':
        return new Date(option.value).toLocaleDateString();
      case 'time':
        return new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'location':
        return option.value;
      default:
        return option.value;
    }
  };

  const getAvailabilityColor = (status: AvailabilityStatus) => {
    switch (status) {
      case 'free':
        return 'bg-green-500';
      case 'tentative':
        return 'bg-orange-500';
      case 'busy':
        return 'bg-red-500';
      case 'not_connected':
      case 'unknown':
        return 'bg-gray-400';
      default:
        return 'bg-gray-300';
    }
  };

  const recommendedOptions = optionAnalysis.filter(a => a.isRecommended);
  const hasAvailableOptions = recommendedOptions.length > 0;

  // Don't render if feature is disabled
  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-primary" />
            Smart Voting Assistant
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasAvailableOptions ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Found {recommendedOptions.length} recommended option(s) based on your availability
              </div>
              
              <Button 
                onClick={handleQuickVoteAvailable}
                className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                size="lg"
              >
                <Zap className="w-4 h-4 mr-2" />
                Quick Vote Available Times
              </Button>
              
              <div className="grid grid-cols-2 gap-2">
                {recommendedOptions.slice(0, 4).map(analysis => (
                  <Badge 
                    key={analysis.optionId}
                    variant="secondary" 
                    className="justify-center py-2 bg-green-100 text-green-800 border-green-200"
                  >
                    {formatOptionValue(analysis.option)}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                No perfectly available times found - review options below
              </div>
              
              <Button 
                variant="outline"
                onClick={() => {
                  // Select best options even if not perfectly available
                  onSuggestedOptionsSelect(bestTimeSlots);
                }}
                className="w-full"
                disabled={bestTimeSlots.length === 0}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Vote for Best Available Options
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group Availability Heatmap */}
      {showHeatmap && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Group Availability Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {event.options.map(option => {
                const availability = groupAvailability[option.id] || 0;
                const personalStatus = availabilityData[option.id] || 'unknown';
                
                return (
                  <div key={option.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getAvailabilityColor(personalStatus)}`} />
                        <span className="font-medium">
                          {formatOptionValue(option)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {availability}% available
                        </span>
                        <Users className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Progress 
                            value={availability} 
                            className="h-2"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{availability}% of guests are available for this option</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                Legend
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span>You're available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>You're busy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span>Partial conflict</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span>Unknown/No calendar</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Best Time Suggestions */}
      {bestTimeSlots.length > 0 && (
        <Card className="border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-accent" />
              Recommended for You
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {bestTimeSlots.map(optionId => {
                const analysis = optionAnalysis.find(a => a.optionId === optionId);
                const groupAvail = groupAvailability[optionId] || 0;
                
                if (!analysis) return null;
                
                return (
                  <div 
                    key={optionId}
                    className="flex items-center justify-between p-3 bg-accent/5 rounded-lg border border-accent/20"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium">
                        {formatOptionValue(analysis.option)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        {Math.round(analysis.availabilityScore)}% match
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {groupAvail}% group
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};